import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { apiCall } from "../api/apiCall";
import { useTranslation } from "../i18n/UseTranslation";
import { getAuth, onAuthStateChanged } from "firebase/auth";

import "./UpgradePage.css";

type BillingPeriod = "monthly" | "yearly";

type PlanCode = "free" | "essential" | "business" | "enterprise";

interface PlanFeature {
  label: string;
  included: boolean;
}

interface SubscriptionPlan {
  code: PlanCode;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyMonthlyPrice: number;
  employeeLimit: number;
  expenseLimit: number | null;
  highlighted?: boolean;
  badge?: string;
  features: PlanFeature[];
}

const UpgradePage: React.FC = () => {
  const navigate = useNavigate();

  const { t, language } = useTranslation();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");

  const [loadingPlan, setLoadingPlan] = useState<PlanCode | null>(null);

  const [error, setError] = useState<string | null>(null);

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const { data: me } = useQuery<any>({
    queryKey: ["fetchCurrentUser"],
    queryFn: () => apiCall<any>("/users/me"),
    enabled: isAuthReady,
  });

  const currentEmployeeCount: number = me?.company_employee_count ?? 0;

  const locale =
    language === "en" ? "en-US" : language === "es" ? "es-ES" : "fr-FR";

  const plans = useMemo<SubscriptionPlan[]>(
    () => [
      {
        code: "free",
        name: t.upgrade.plans.free.name,
        description: t.upgrade.plans.free.description,
        monthlyPrice: 0,
        yearlyMonthlyPrice: 0,
        employeeLimit: 3,
        expenseLimit: 5,
        features: [
          {
            label: t.upgrade.plans.free.features.employees,
            included: true,
          },
          {
            label: t.upgrade.plans.free.features.expenses,
            included: true,
          },
          {
            label: t.upgrade.plans.free.features.manualCreation,
            included: true,
          },
          {
            label: t.upgrade.plans.free.features.automaticScan,
            included: true,
          },
          {
            label: t.upgrade.plans.free.features.validation,
            included: false,
          },
          {
            label: t.upgrade.plans.free.features.accountingExports,
            included: false,
          },
          {
            label: t.upgrade.plans.free.features.prioritySupport,
            included: false,
          },
        ],
      },

      {
        code: "essential",
        name: t.upgrade.plans.essential.name,
        description: t.upgrade.plans.essential.description,
        monthlyPrice: 89.99,
        yearlyMonthlyPrice: 80.99,
        employeeLimit: 10,
        expenseLimit: 20,
        features: [
          {
            label: t.upgrade.plans.essential.features.employees,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.expenses,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.automaticScan,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.validation,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.accountingExports,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.dashboard,
            included: true,
          },
          {
            label: t.upgrade.plans.essential.features.emailSupport,
            included: true,
          },
        ],
      },

      {
        code: "business",
        name: t.upgrade.plans.business.name,
        description: t.upgrade.plans.business.description,
        monthlyPrice: 159.99,
        yearlyMonthlyPrice: 143.99,
        employeeLimit: 30,
        expenseLimit: 20,
        highlighted: true,
        badge: t.upgrade.mostPopular,
        features: [
          {
            label: t.upgrade.plans.business.features.employees,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.expenses,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.automaticScan,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.advancedValidation,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.accountingExports,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.roles,
            included: true,
          },
          {
            label: t.upgrade.plans.business.features.prioritySupport,
            included: true,
          },
        ],
      },

      {
        code: "enterprise",
        name: t.upgrade.plans.enterprise.name,
        description: t.upgrade.plans.enterprise.description,
        monthlyPrice: 379.99,
        yearlyMonthlyPrice: 341.99,
        employeeLimit: 50,
        expenseLimit: 20,
        features: [
          {
            label: t.upgrade.plans.enterprise.features.employees,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.expenses,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.businessFeatures,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.personalSupport,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.bulkImport,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.customConfiguration,
            included: true,
          },
          {
            label: t.upgrade.plans.enterprise.features.dedicatedSupport,
            included: true,
          },
        ],
      },
    ],
    [t],
  );

  const getDisplayedPrice = (plan: SubscriptionPlan): number => {
    return billingPeriod === "yearly"
      ? plan.yearlyMonthlyPrice
      : plan.monthlyPrice;
  };

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (plan.code === "free") {
      navigate("/dashboard");
      return;
    }

    const stripePriceIds: Record<string, string> = {
      essential_monthly: process.env.REACT_APP_PRICE_ESSENTIAL_MONTHLY || "",

      essential_yearly: process.env.REACT_APP_PRICE_ESSENTIAL_YEARLY || "",

      business_monthly: process.env.REACT_APP_PRICE_BUSINESS_MONTHLY || "",

      business_yearly: process.env.REACT_APP_PRICE_BUSINESS_YEARLY || "",

      enterprise_monthly: process.env.REACT_APP_PRICE_ENTERPRISE_MONTHLY || "",

      enterprise_yearly: process.env.REACT_APP_PRICE_ENTERPRISE_YEARLY || "",
    };

    const priceId = stripePriceIds[`${plan.code}_${billingPeriod}`];

    if (!priceId) {
      setError(t.upgrade.errors.planNotConfigured);
      return;
    }

    try {
      setLoadingPlan(plan.code);
      setError(null);

      const response = await apiCall<{
        url: string;
      }>("/subscriptions/create-checkout", {
        method: "POST",
        body: JSON.stringify({
          price_id: priceId,
        }),
      });

      if (!response.url) {
        throw new Error(t.upgrade.errors.checkoutUrlMissing);
      }

      window.location.href = response.url;
    } catch (checkoutError) {
      console.error("[UpgradePage] Checkout error:", checkoutError);

      setError(t.upgrade.errors.checkoutFailed);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <main className="upgrade-page">
      <div className="upgrade-background-glow upgrade-glow-left" />
      <div className="upgrade-background-glow upgrade-glow-right" />

      <button
        type="button"
        className="upgrade-back-button"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        {t.upgrade.back}
      </button>

      <section className="upgrade-hero">
        <div className="upgrade-rating">
          <span>★★★★★</span>

          <small>{t.upgrade.ratingText}</small>
        </div>

        <div className="upgrade-title-icon">
          <Sparkles size={24} />
        </div>

        <h1>
          {t.upgrade.title}
          <span> {t.upgrade.titleHighlight}</span>
        </h1>

        <p>{t.upgrade.subtitle}</p>

        <div className="billing-switch">
          <button
            type="button"
            className={billingPeriod === "monthly" ? "active" : ""}
            onClick={() => setBillingPeriod("monthly")}
          >
            {t.upgrade.monthly}
          </button>

          <button
            type="button"
            className={billingPeriod === "yearly" ? "active" : ""}
            onClick={() => setBillingPeriod("yearly")}
          >
            {t.upgrade.yearly}

            <span>{t.upgrade.yearlyDiscount}</span>
          </button>
        </div>
      </section>

      {error && <div className="upgrade-error">{error}</div>}

      <section className="pricing-grid">
        {plans.map((plan) => {
          const displayedPrice = getDisplayedPrice(plan);

          const isLoading = loadingPlan === plan.code;

          const isTooSmall =
            plan.code !== "free" && currentEmployeeCount > plan.employeeLimit;

          return (
            <article
              key={plan.code}
              className={[
                "pricing-card",
                plan.highlighted ? "pricing-card-highlighted" : "",
                isTooSmall ? "pricing-card-disabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {plan.badge && (
                <div className="pricing-popular-badge">{plan.badge}</div>
              )}

              <div className="pricing-card-header">
                <span className="pricing-plan-label">
                  {plan.highlighted && <Sparkles size={15} />}

                  {plan.name}
                </span>

                <div className="pricing-price">
                  <strong>{displayedPrice.toLocaleString(locale)} €</strong>

                  {displayedPrice > 0 && <span>{t.upgrade.perMonth}</span>}
                </div>

                {billingPeriod === "yearly" && displayedPrice > 0 && (
                  <p className="pricing-yearly-total">
                    {t.upgrade.billed}{" "}
                    {(displayedPrice * 12).toLocaleString(locale)} €{" "}
                    {t.upgrade.perYear}
                  </p>
                )}

                {plan.code === "free" && (
                  <p className="pricing-yearly-total">
                    {t.upgrade.noPaymentRequired}
                  </p>
                )}

                <p className="pricing-description">{plan.description}</p>

                {isTooSmall && (
                  <p className="pricing-disabled-notice">
                    {t.upgrade.tooManyEmployees
                      ? t.upgrade.tooManyEmployees
                          .replace("{count}", String(currentEmployeeCount))
                          .replace("{limit}", String(plan.employeeLimit))
                      : `Votre entreprise compte ${currentEmployeeCount} employé(s), ce forfait est limité à ${plan.employeeLimit}.`}
                  </p>
                )}
              </div>

              <button
                type="button"
                className={[
                  "pricing-select-button",
                  plan.highlighted ? "pricing-select-button-highlighted" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                disabled={loadingPlan !== null || isTooSmall}
                onClick={() => handleSelectPlan(plan)}
              >
                {isLoading
                  ? t.upgrade.redirecting
                  : plan.code === "free"
                    ? t.upgrade.continueFree
                    : t.upgrade.choosePlan}
              </button>

              <div className="pricing-divider" />

              <ul className="pricing-features">
                {plan.features.map((feature) => (
                  <li
                    key={feature.label}
                    className={feature.included ? "" : "disabled-feature"}
                  >
                    <span className="pricing-feature-icon">
                      {feature.included ? <Check size={17} /> : <X size={16} />}
                    </span>

                    <span>{feature.label}</span>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </section>

      <section className="pricing-reassurance">
        <div>
          <strong>{t.upgrade.reassurance.securePaymentTitle}</strong>

          <span>{t.upgrade.reassurance.securePaymentText}</span>
        </div>

        <div>
          <strong>{t.upgrade.reassurance.noCommitmentTitle}</strong>

          <span>{t.upgrade.reassurance.noCommitmentText}</span>
        </div>

        <div>
          <strong>{t.upgrade.reassurance.helpTitle}</strong>

          <span>{t.upgrade.reassurance.helpText}</span>
        </div>
      </section>
    </main>
  );
};

export default UpgradePage;
